import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { domainLocaleMap, HxAuthService, Language, PromoBannerModel, PromoStory, UiLabel } from 'hx-services';
import { NzUploadChangeParam, NzUploadComponent, NzUploadFile } from 'ng-zorro-antd/upload';
import { firstValueFrom, from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildGooglePublicUrl, getBase64 } from '@adm-web-app/utils';
import { HttpClient } from '@angular/common/http';
import { NzModalFooterDirective, NzModalRef } from 'ng-zorro-antd/modal';
import { PromoBannerService } from '@adm-web-app/services/promo-banner.service';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NgIf } from '@angular/common';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { HxLabelEditComponent } from 'hx-components';

/** The longest text on a frame that the backend takes (`catalog.story.textTooLong` above): the title and the line under it. */
const STORY_TITLE_MAX = 80;
const STORY_SUBTITLE_MAX = 200;

interface PreviewFile {
  fileName?: string;
  link?: string;
  data?: string;
  image?: string;
  visible?: boolean;
}

@Component({
    selector: 'hc-story-edit-modal',
    templateUrl: './story-edit.modal.html',
    styleUrls: ['./story-edit.modal.less'],
  imports: [TranslocoDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NgIf, NzSkeletonComponent, NzUploadComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzAlertComponent, NzFormLabelComponent, NzFormControlComponent, NzInputDirective, NzSelectComponent, NzOptionComponent, HxLabelEditComponent, NzModalFooterDirective, TranslocoPipe]
})
export class StoreEditModal implements OnInit {
  @Input() promo!: PromoBannerModel;
  @Input() story?: PromoStory;
  langs = Array.from(domainLocaleMap.keys());
  titleMax = STORY_TITLE_MAX;
  subtitleMax = STORY_SUBTITLE_MAX;

  form = new FormGroup({
    url: new FormControl<string | null>(null, [Validators.required]),
    type: new FormControl<string | null>(null, [Validators.required]),
    actionUrl: new FormControl<string | null>(null),
    actionButton: new FormControl<UiLabel | null>(null),
    lang: new FormControl<string | null>(null, [Validators.required]),
    // the text on the frame, in the language of the frame (`lang`); plain text, the site shows it as text
    title: new FormControl<string | null>(null, [Validators.maxLength(STORY_TITLE_MAX)]),
    subtitle: new FormControl<string | null>(null, [Validators.maxLength(STORY_SUBTITLE_MAX)]),
  });
  isLoading = {
    files: false,
    save: false,
  };
  preview: PreviewFile = {};
  fileList: NzUploadFile[] = [];
  filesUploadUrl!: string;

  constructor(
    private http: HttpClient,
    public modalRef: NzModalRef,
    private promoBannerService: PromoBannerService,
    private auth: HxAuthService,
  ) {
  }

  ngOnInit(): void {
    console.log('promo', this.promo);
    this.filesUploadUrl = this.promoBannerService.getFileUploadUrl(this.promo.name) + '?story=true';
    if (this.story) {
      this.form.patchValue({
        url: this.story.url,
        type: this.story.type,
        actionButton: this.story.action?.button,
        actionUrl: this.story.action?.url,
        lang: this.story.lang,
        title: this.story.title ?? null,
        subtitle: this.story.subtitle ?? null,
      });
    }
  }

  submitForm() {
    Object.values(this.form.controls).forEach(value => {
      value.markAsDirty();
      value.updateValueAndValidity();
    });

    if (this.form.valid) {
      const formModel = this.form.value;
      if (!formModel.url || !formModel.type) {
        throw new Error('required fields');
      }
      const story: PromoStory = {
        url: formModel.url,
        type: formModel.type,
        lang: formModel.lang!,
      };
      const title = formModel.title?.trim();
      const subtitle = formModel.subtitle?.trim();
      if (title) {
        story.title = title;
      }
      if (subtitle) {
        story.subtitle = subtitle;
      }
      if (formModel.actionUrl && formModel.actionButton) {
        story.action = {
          url: formModel.actionUrl,
          button: formModel.actionButton,
        };
      }
      this.modalRef.close(story);
    }
  }

  handlePreviewFn = async (file: NzUploadFile) => {
    if (!file.url && !file['preview'] && file.originFileObj) {
      file['preview'] = await getBase64(file.originFileObj);
    }
    this.preview = {};
    this.preview.image = file.url ?? file['preview'];
    this.preview.visible = true;
    this.preview.fileName = file.filename;
    if (this.preview.fileName?.toLowerCase().endsWith('.md') && file.url) {
      this.preview.image = undefined;
      const response = await firstValueFrom(this.http.get(file.url, {responseType: 'text'}));
      if (typeof response === 'string') {
        this.preview.data = response;
      }
    }

    this.preview.link = this.getDownloadUrl(file.name);
  };

  fileUploadHeadersFn = () => {
    const token = this.auth.keycloakToken();
    return {
      Authorization: `Bearer ${token}`,
      'ngsw-bypass': 'true'
    };
  };

  fileDownloadFn = (file: NzUploadFile) => window.open(this.getDownloadUrl(file.name), '_blank');

  handleFileUploadStatusChanged(event: NzUploadChangeParam) {
    if (event.type === 'success' || event.type === 'removed') {
      this.fileList = event.fileList.length > 0 ? [event.fileList[0]] : [];
      if (event.type === 'success') {
        const contentType: string = event.file.response?.contentType;
        if (contentType.startsWith('image')) {
          this.form.controls.type.setValue('image');
        } else if (contentType.startsWith('video')) {
          this.form.controls.type.setValue('video');
        }
        this.form.controls.url.setValue(this.getDownloadUrl(event.file.name));
      }
      this.form.markAsDirty();
    }
  }

  private getDownloadUrl(fileName: string) {
    return 'https://storage.googleapis.com/' + buildGooglePublicUrl(`promo/${this.promo.name}/stories/${fileName}`);
  }
}
